import * as React from "react";
import {observer, inject} from "mobx-react";
import {Table} from "react-bootstrap";
import {
    getCoreRowModel,
    useReactTable,
    flexRender,
    ColumnDef,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    } from "@tanstack/react-table";
import {Utils} from "../../common/utils";
import {NotificationViewerStore, ViewerUserNotificacion} from "Stores/notificationViewersStore";
import { BellIcon } from "Components/Icons/BellIcon";
import { BellSlashIcon } from "Components/Icons/BellSlashIcon";
import { ArrowDownWideIcon } from "Components/Icons/ArrowDownWideIcon";
import { ArrowUpWideIcon } from "Components/Icons/ArrowUpWideIcon";
import { AngleLeftIcon } from "Components/Icons/AngleLeftIcon";
import { AngleRightIcon } from "Components/Icons/AngleRightIcon";
interface ViewersListProps {
    notificationViewerStore?: NotificationViewerStore
    enableNotifications: (user_id: string) => void
    deactivateNotifications: (user_id: string) => void
}

export interface RowPinningState {
    bottom?: string[];
    top?: string[];
}

interface TicketViewerListState {
    users: ViewerUserNotificacion[]
}
@inject("notificationViewerStore")
@observer
export class TicketViewersList extends React.Component<ViewersListProps, TicketViewerListState> {
    userChosenSchoolInfo = Utils.getChosenSchoolInfo();
    ADMIN_USER = "admin";

    constructor(props) {
        super(props);
        this.state = {
            users: this.props.notificationViewerStore?.viewersNotifications as ViewerUserNotificacion[]
        };
    }

    columns: ColumnDef<ViewerUserNotificacion>[] = [
        {
            header: () => "Name",
            id: "fullName",
            accessorFn: (viewer) => {return (`${viewer.user.first_name} ${viewer.user.last_name} ${viewer.is_current_user ? "(Me)": ""}`);}
        },
        {
            header: () => "Attributes",
            cell: info => <div
            className={`attribute-chip 
                ${info.row.original.role === this.ADMIN_USER ? "admin" : ""}`}
                >
                    {info.row.original.role}
                </div>,
            accessorKey: "role"
        },
        {
            header: () => "Notifications",
            accessorKey: "enabled",
            cell: info => this.renderBell(
                info.renderValue() as boolean,
                String(info.row.original.user_ruuid),
                info.row.original.is_current_user)
        }
    ];

    renderBell = (enabled_notification: boolean, user_id: string, is_current_user: boolean) => {
        if (this.userChosenSchoolInfo === undefined) {
            return null;
        } else {
            return (
                <div className="bell-container">
                    {
                        this.userChosenSchoolInfo.is_admin || is_current_user? (
                            <div>
                                {enabled_notification ?
                                    <BellIcon
                                        className="icon-bell"
                                        data-testid="active-bell"
                                        onClick={() => this.props.deactivateNotifications(user_id)} /> :
                                    <BellSlashIcon
                                        onClick={() => this.props.enableNotifications(user_id)}
                                        data-testid="deactivate-bell"
                                        className="icon-bell-slash"
                                    />
                                }
                            </div>) :
                            (<div>
                                {enabled_notification ? "On" : "Off"}
                            </div>)
                    }
                </div>
            );
        }
    };

    render() {
        return (
            <fieldset className="viewersMargin">
                <legend className="viewersMargin">Viewers List</legend>
                <RenderViewers
                    columns={this.columns}
                    defaultData={this.props.notificationViewerStore?.viewersNotifications as ViewerUserNotificacion[]}
                    pageIndex={this.props.notificationViewerStore?.pageIndex ?? 0}
                    setPageIndex={(page) => this.props.notificationViewerStore?.setPageIndex(page)} />
            </fieldset>
        );
    }
}


interface RenderViewersProps {
    columns: ColumnDef<ViewerUserNotificacion>[]
    defaultData: ViewerUserNotificacion[]
    pageIndex: number
    setPageIndex: (page: number) => void
}
const RenderViewers = ({columns, defaultData, pageIndex, setPageIndex}: RenderViewersProps) => {
    const [data, setData] = React.useState(() => [...defaultData]);
    const [sorting, setSorting] = React.useState<SortingState>([]);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
                    },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),

    });

    React.useEffect(() => {
        if (data !== defaultData) {
            setData([...defaultData]);
        }
    }, [defaultData]);

    React.useEffect(() => {
        table.setPageIndex(pageIndex);
    }, [data]);

    return (
        <div className="viewers-container">
            <Table>
                <thead>
                    {table.getHeaderGroups().map((groupHeader) => (
                        <tr key={groupHeader.id}>
                            {groupHeader.headers.map((header) => {
                                return (
                                    <th className={`th_${header.id} viewers-head`} key={header.id} onClick={header.column.getToggleSortingHandler()}>
                                        <div className={`table_${header.id}`}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            {{
                                                asc: <ArrowDownWideIcon className="icon-bell" />,
                                                desc: <ArrowUpWideIcon className="icon-bell" />,
                                            }[header.column.getIsSorted() as string] ?? null}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map(row => (
                        <tr className="viewers-row" key={row.id}>
                            {row.getVisibleCells().map((cell) => {
                                return (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    <tr>
                        <td colSpan={12} className="nav-row">
                            <div className="pages-nav">
                                {table.getState().pagination.pageIndex === 0 ? table.getState().pagination.pageIndex + 1 : (table.getState().pagination.pageIndex * 10) + 1}
                                {" "}-{" "}
                                {table.getCanNextPage()? table.getState().pagination.pageIndex === 0 ? table.getState().pagination.pageSize : (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize : data.length}
                                {" "}of{" "}{data.length}
                                <AngleLeftIcon
                                    className="icon-angle"
                                    onClick={() => {
                                        table.getCanPreviousPage() &&
                                        setPageIndex(table.getState().pagination.pageIndex - 1);
                                        table.previousPage();
                                    }}
                                />
                                <AngleRightIcon
                                    className="icon-angle"
                                    onClick={() => {
                                        table.getCanNextPage() &&
                                        (setPageIndex(table.getState().pagination.pageIndex + 1),
                                        table.nextPage());
                                    }}
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </Table>
        </div>
    );
};
